import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { suppliersAPI, onboardingAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { WIZARD_STEPS } from './types';
import type { SupplierFormData } from './types';

interface UseWizardActionsParams {
  id: string | undefined;
  isNewSupplier: boolean;
  formData: SupplierFormData;
  currentStep: number;
  highestCompletedStep: number;
  createdSupplierId: number | null;
  workflowId: number | null;
  riskDatasetId: number | null;
  riskQuestions: Array<{
    id: string;
    question: string;
    type: string;
    options: string[];
    scores?: Record<string, number>;
    max_score?: number;
  }>;
  declineReason: string;
  declineNotes: string;
  setSaving: (v: boolean) => void;
  setCreatedSupplierId: (v: number) => void;
  setSupplierStatus: (v: string) => void;
  setHighestCompletedStep: (v: number) => void;
  setCurrentStep: (v: number | ((prev: number) => number)) => void;
  setShowDeclineModal: (v: boolean) => void;
  setShowApproveModal: (v: boolean) => void;
  validateStep: (step: number) => string | null;
}

interface UseWizardActionsReturn {
  ensureSupplierCreated: () => Promise<number>;
  saveDraft: (nextStep?: number, nextHighest?: number) => Promise<void>;
  handleSave: () => Promise<void>;
  handleDecline: () => Promise<void>;
  handleApprove: () => Promise<void>;
  handleSendInvite: () => Promise<void>;
}

/**
 * Custom hook encapsulating the wizard's async action handlers:
 * ensureSupplierCreated, saveDraft, handleSave, handleDecline, handleApprove, handleSendInvite.
 *
 * @param params - All state and setter dependencies needed by the actions
 * @returns Object with action handler functions
 */
export function useWizardActions(params: UseWizardActionsParams): UseWizardActionsReturn {
  const {
    id,
    isNewSupplier,
    formData,
    currentStep,
    highestCompletedStep,
    createdSupplierId,
    workflowId,
    riskDatasetId,
    riskQuestions,
    declineReason,
    declineNotes,
    setSaving,
    setCreatedSupplierId,
    setSupplierStatus,
    setHighestCompletedStep,
    setCurrentStep,
    setShowDeclineModal,
    setShowApproveModal,
    validateStep,
  } = params;

  const navigate = useNavigate();

  // Helper: create the supplier if not already created, returns supplier ID
  const ensureSupplierCreated = useCallback(async (): Promise<number> => {
    if (createdSupplierId) return createdSupplierId;
    if (!isNewSupplier) return Number(id);

    // CR-5390: customer_id may now be unset until the user picks a Customer
    // Company in the consolidated section. Fall back to the first
    // customer_ids entry so saveDraft / wizard creation still works.
    const primaryCustomerId = formData.customer_id ?? formData.customer_ids?.[0];
    if (!primaryCustomerId) {
      throw new Error('Please add at least one customer company before saving.');
    }

    const createResult = await suppliersAPI.createFromWizard({
      company_name: formData.company_name,
      company_address: formData.company_address,
      // CR-5390: structured address sent alongside the legacy single-line
      // company_address so older callers keep working while the backend
      // upgrades to use the structured fields when present.
      address_line_1: formData.address_line_1,
      address_line_2: formData.address_line_2,
      address_city: formData.address_city,
      address_region: formData.address_region,
      address_postcode: formData.address_postcode,
      address_country: formData.address_country,
      description: formData.description,
      currency: formData.currency,
      annual_spend: formData.annual_spend,
      logo_url: formData.logo_url,
      customer_id: primaryCustomerId as number,
      customer_ids: formData.customer_ids,
    });

    const newId = createResult.data.supplier_id;
    setCreatedSupplierId(newId);
    setSupplierStatus('draft');
    return newId;
  }, [createdSupplierId, isNewSupplier, id, formData, setCreatedSupplierId, setSupplierStatus]);

  // Save wizard draft to backend so the wizard can be resumed later
  // Uses a ref to always access the latest formData, avoiding stale closure issues
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const saveDraft = useCallback(async (nextStep?: number, nextHighest?: number) => {
    const supplierId = createdSupplierId || (!isNewSupplier ? Number(id) : null);
    if (!supplierId) return;
    try {
      await suppliersAPI.saveWizardDraft(supplierId, {
        form_data: formDataRef.current as unknown as Record<string, unknown>,
        current_step: nextStep ?? currentStep,
        highest_step: nextHighest ?? highestCompletedStep,
      });
    } catch (err) {
      console.error('Failed to save wizard draft:', err);
    }
  }, [createdSupplierId, isNewSupplier, id, currentStep, highestCompletedStep]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);

      const error = validateStep(currentStep);
      if (error) {
        toast.error(error);
        setSaving(false);
        return;
      }

      if (currentStep < WIZARD_STEPS.length) {
        // Auto-save as Draft when progressing beyond Step 2
        if (currentStep >= 2 && isNewSupplier && !createdSupplierId) {
          try {
            await ensureSupplierCreated();
            toast.success('Supplier saved as draft.');
          } catch (err) {
            console.error('Failed to save draft:', err);
          }
        }
        const nextHighest = Math.max(highestCompletedStep, currentStep);
        const nextStep = currentStep + 1;
        setHighestCompletedStep(nextHighest);
        setCurrentStep(nextStep);
        saveDraft(nextStep, nextHighest);
      } else {
        // Final step — create supplier (if needed) then submit for approval
        let supplierId: number;
        try {
          supplierId = await ensureSupplierCreated();
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Failed to create supplier.');
          setSaving(false);
          return;
        }

        // Save draft with final step data (risk answers) before submitting
        const finalHighest = Math.max(highestCompletedStep, currentStep);
        setHighestCompletedStep(finalHighest);
        await saveDraft(currentStep, finalHighest);

        const wizardPayload: Parameters<typeof onboardingAPI.submitWizard>[0] = {
          supplier_id: supplierId,
          customer_id: formData.customer_id as number,
          risk_answers: formData.risk_answers,
          risk_dataset_id: riskDatasetId,
          annual_spend: formData.annual_spend || null,
        };
        if (!riskDatasetId && Object.keys(formData.risk_answers).length > 0) {
          wizardPayload.risk_questions = riskQuestions.map(q => ({
            id: q.id,
            question: q.question,
            type: q.type,
            options: q.options,
            scores: q.scores,
            max_score: q.max_score,
          }));
        }
        const result = await onboardingAPI.submitWizard(wizardPayload);

        setSupplierStatus('pending_initial');
        toast.success('Supplier submitted for approval.');
        if (result.data.can_self_approve) {
          toast.info('You can approve this supplier now from the approval page.');
        }
        // CR-4758: After submitting a new supplier for approval, drop the
        // user on the Pending Approvals tab so they can act on it directly,
        // rather than the generic Suppliers list.
        navigate('/onboarding?tab=approvals');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    currentStep, highestCompletedStep, isNewSupplier, createdSupplierId,
    formData, riskDatasetId, riskQuestions, setSaving, setHighestCompletedStep,
    setCurrentStep, setSupplierStatus, validateStep, ensureSupplierCreated,
    saveDraft, navigate,
  ]);

  const handleDecline = useCallback(async () => {
    try {
      setSaving(true);
      if (!workflowId) {
        toast.error('No onboarding workflow found.');
        setShowDeclineModal(false);
        return;
      }
      await onboardingAPI.declineWorkflow(workflowId, declineNotes || declineReason);
      setSupplierStatus('not_approved');
      setShowDeclineModal(false);
      toast.success('Supplier declined.');
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to decline:', error);
      toast.error('Failed to decline supplier.');
    } finally {
      setSaving(false);
    }
  }, [workflowId, declineNotes, declineReason, setSaving, setSupplierStatus, setShowDeclineModal, navigate]);

  const handleApprove = useCallback(async () => {
    try {
      setSaving(true);
      if (!workflowId) {
        toast.error('No onboarding workflow found.');
        setShowApproveModal(false);
        return;
      }
      await onboardingAPI.approveWorkflow(workflowId);
      setSupplierStatus('approved');
      setShowApproveModal(false);
      toast.success('Supplier approved successfully.');
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve supplier.');
    } finally {
      setSaving(false);
    }
  }, [workflowId, setSaving, setSupplierStatus, setShowApproveModal, navigate]);

  const handleSendInvite = useCallback(async () => {
    try {
      setSaving(true);
      if (!id || isNewSupplier) {
        toast.error('Cannot send invite without a saved supplier.');
        return;
      }
      const inviteResult = await onboardingAPI.createInvitation({
        supplier: Number(id),
        invitee_company_name: formData.company_name,
        invitee_contact_name: `${formData.contact_first_name} ${formData.contact_last_name}`.trim(),
        invitee_email: formData.contact_email,
      });
      await onboardingAPI.sendInvitation(inviteResult.data.id);
      setSupplierStatus('invited');
      toast.success('Invitation sent to supplier.');
    } catch (error) {
      console.error('Failed to send invite:', error);
      toast.error('Failed to send invitation.');
    } finally {
      setSaving(false);
    }
  }, [id, isNewSupplier, formData, setSaving, setSupplierStatus]);

  return {
    ensureSupplierCreated,
    saveDraft,
    handleSave,
    handleDecline,
    handleApprove,
    handleSendInvite,
  };
}
